import React, { useEffect, useState } from "react";
import Card from "src/components/Cards/Basic";
import { Button } from "react-bootstrap";
import axios from "src/config/axios";
import { useParams } from "react-router-dom";
import useBusiness from "src/pages/AdminDashboard/Businesses/util/useBusiness";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import InputText from "src/components/Forms/InputText";
import SelectBox from "src/components/Forms/SelectBox";

const DEFAULT_MAPPING = {
  fiscal_key: "default",
  tax_rate: "tax_10",
  department_nr: 1,
  is_default: false,
};

const FISCAL_KEY_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "deposit_card", label: "Deposit Card" },
  { value: "local_tax", label: "Local Tax" },
  { value: "wc_wash", label: "WC Wash" },
];

const TAX_RATE_OPTIONS = [
  { value: "tax_0", label: "0 %" },
  { value: "tax_4", label: "4 %" },
  { value: "tax_10", label: "10 %" },
  { value: "tax_22", label: "22 %" },
];

const FiscalDepartmentSettingsPage = () => {
  const [fiscalDepartmentMappings, setFiscalDepartmentMappings] = useState([]);
  const [draftMappings, setDraftMappings] = useState({});
  const [newMapping, setNewMapping] = useState(DEFAULT_MAPPING);
  const { id } = useParams();
  const business = useBusiness(id);

  useEffect(() => {
    loadFiscalDepartmentMappings();
  }, [id]);

  const loadFiscalDepartmentMappings = () => {
    axios
      .get(`/api/v1/admin/businesses/${id}/fiscal_department_mappings.json`)
      .then(({ data }) => {
        const entries = data.entries ?? [];
        setFiscalDepartmentMappings(entries);

        const nextDrafts = {};
        entries.forEach((entry) => {
          nextDrafts[entry.id] = {
            fiscal_key: entry.fiscal_key,
            tax_rate: entry.tax_rate,
            department_nr: entry.department_nr,
            is_default: entry.is_default,
          };
        });

        setDraftMappings(nextDrafts);
      })
      .catch(() => showErrorToast());
  };

  const updateDraft = (id, key, value) => {
    setDraftMappings((previous) => ({
      ...previous,
      [id]: {
        ...previous[id],
        [key]: value,
      },
    }));
  };

  const createMapping = () => {
    axios
      .post(`/api/v1/admin/businesses/${id}/fiscal_department_mappings.json`, {
        fiscal_department_mapping: newMapping,
      })
      .then(() => {
        showSuccessToast();
        setNewMapping(DEFAULT_MAPPING);
        loadFiscalDepartmentMappings();
      })
      .catch(() => showErrorToast());
  };

  const updateMapping = (mappingId) => {
    const mapping = draftMappings[mappingId];
    if (!mapping) return;

    axios
      .put(
        `/api/v1/admin/businesses/${id}/fiscal_department_mappings/${mappingId}.json`,
        { fiscal_department_mapping: mapping }
      )
      .then(() => {
        showSuccessToast();
        loadFiscalDepartmentMappings();
      })
      .catch(() => showErrorToast());
  };

  const deleteMapping = (mappingId) => {
    axios
      .delete(
        `/api/v1/admin/businesses/${id}/fiscal_department_mappings/${mappingId}.json`
      )
      .then(() => {
        showSuccessToast();
        loadFiscalDepartmentMappings();
      })
      .catch((response) => showValidationErrorToast(response));
  };

  return (
    <Card title={`Steuersätze von ${business?.name ?? ""}`}>
      <div className="table-responsive">
        <table className="table table-bordered" width="100%" cellSpacing="0">
          <thead>
            <tr>
              <th>Fiscal Key</th>
              <th>Tax Rate</th>
              <th>Dipartimento</th>
              <th>Default</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {fiscalDepartmentMappings.map((mapping) => (
              <tr key={mapping.id}>
                <td>
                  <SelectBox
                    value={draftMappings[mapping.id]?.fiscal_key ?? "default"}
                    onChange={(e) =>
                      updateDraft(mapping.id, "fiscal_key", e.target.value)
                    }
                    options={FISCAL_KEY_OPTIONS}
                    option_name="label"
                    option_value="value"
                  />
                </td>
                <td>
                  <SelectBox
                    value={draftMappings[mapping.id]?.tax_rate ?? "tax_10"}
                    onChange={(e) =>
                      updateDraft(mapping.id, "tax_rate", e.target.value)
                    }
                    options={TAX_RATE_OPTIONS}
                    option_name="label"
                    option_value="value"
                  />
                </td>
                <td>
                  <InputText
                    type="number"
                    min={0}
                    value={draftMappings[mapping.id]?.department_nr ?? 0}
                    onChange={(e) =>
                      updateDraft(
                        mapping.id,
                        "department_nr",
                        Number(e.target.value)
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={Boolean(draftMappings[mapping.id]?.is_default)}
                    onChange={(e) =>
                      updateDraft(mapping.id, "is_default", e.target.checked)
                    }
                  />
                </td>
                <td>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => updateMapping(mapping.id)}
                  >
                    Speichern
                  </Button>
                  <Button
                    className="ml-2"
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteMapping(mapping.id)}
                  >
                    Löschen
                  </Button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={5} className="bg-light">
                <strong>Neues Mapping (noch nicht gespeichert)</strong>
              </td>
            </tr>
            <tr>
              <td>
                <SelectBox
                  value={newMapping.fiscal_key}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewMapping((previous) => ({
                      ...previous,
                      fiscal_key: value,
                    }));
                  }}
                  options={FISCAL_KEY_OPTIONS}
                  option_name="label"
                  option_value="value"
                />
              </td>
              <td>
                <SelectBox
                  value={newMapping.tax_rate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewMapping((previous) => ({
                      ...previous,
                      tax_rate: value,
                    }));
                  }}
                  options={TAX_RATE_OPTIONS}
                  option_name="label"
                  option_value="value"
                />
              </td>
              <td>
                <InputText
                  type="number"
                  min={0}
                  value={newMapping.department_nr}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setNewMapping((previous) => ({
                      ...previous,
                      department_nr: value,
                    }));
                  }}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={newMapping.is_default}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setNewMapping((previous) => ({
                      ...previous,
                      is_default: value,
                    }));
                  }}
                />
              </td>
              <td>
                <Button variant="primary" size="sm" onClick={createMapping}>
                  Neu erstellen
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {fiscalDepartmentMappings.length === 0 && (
        <small>Keine Fiscal Department Mappings vorhanden.</small>
      )}
    </Card>
  );
};

export default FiscalDepartmentSettingsPage;
